import React, { useState, useEffect } from 'react';
import './OrderNow.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaComment, FaAngleDown, FaClock, FaWater, FaRulerCombined } from 'react-icons/fa';
import { FaLocationDot, FaBottleWater } from 'react-icons/fa6';

import axios from "axios";

const API_PATH = 'https://aquadrop-react.asdesignsgalaxy.com/api/phpmailer/order.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const OrderNow = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    locationType: '',
    address: '',
    waterType: '',
    containerType: '',
    quantity: '',
    frequency: '',
    startDate: '',
    deliveryTime: '',
    customRequests: '',
    additionalServices: []
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.locationType) validationErrors.push('Location type is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.waterType) validationErrors.push('Water type is required');
      if (!formData.containerType) validationErrors.push('Container type is required');
      if (!formData.quantity) validationErrors.push('Quantity is required');
      if (!formData.frequency) validationErrors.push('Frequency is required');
      if (!formData.startDate) validationErrors.push('Start date is required');
      if (!formData.deliveryTime) validationErrors.push('Delivery time is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        locationType: '',
        address: '',
        waterType: '',
        containerType: '',
        quantity: '',
        frequency: '',
        startDate: '',
        deliveryTime: '',
        customRequests: '',
        additionalServices: []
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, additionalServices: [...prev.additionalServices, value] };
      } else {
        return { ...prev, additionalServices: prev.additionalServices.filter(item => item !== value) };
      }
    });
  };

  return (
    <section className="order-now">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Place an Order'}  subTitle={"Schedule Your Water Delivery Now"} linear sideHeading />

            <div className="input-box">
              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Location Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="locationType"><FaLocationDot /></label>
                  <select
                    className="select-box"
                    id="locationType"
                    name="locationType"
                    value={formData.locationType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Type of Location</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Office">Office</option>
                    <option value="Event">Event</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            {/* Address Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="address"><FaLocationDot /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Water Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="waterType"><FaWater /></label>
                  <select
                    className="select-box"
                    id="waterType"
                    name="waterType"
                    value={formData.waterType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Water Type</option>
                    <option value="Spring">Spring Water</option>
                    <option value="Purified">Purified Water</option>
                    <option value="Mineral">Mineral Water</option>
                    <option value="Distilled">Distilled Water</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Container Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="containerType"><FaBottleWater /></label>
                  <select
                    className="select-box"
                    id="containerType"
                    name="containerType"
                    value={formData.containerType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Container Type</option>
                    <option value="5-gallon">5-Gallon Bottles</option>
                    <option value="1-gallon">1-Gallon Bottles</option>
                    <option value="Small Bottles">Small Bottles (500ml)</option>
                    <option value="Custom">Custom</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              {/* Quantity Input Field */}
              <div className="input-field">
                <label htmlFor="quantity"><FaRulerCombined /></label>
                <input
                  type="number"
                  name="quantity"
                  className="box"
                  id="quantity"
                  placeholder="Quantity Required"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Frequency Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="frequency"><FaClock /></label>
                  <select
                    className="select-box"
                    id="frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Frequency of Delivery</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-Weekly">Bi-Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="One-Time">One-Time</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              {/* Start Date Input Field */}
              <div className="input-field">
                <label htmlFor="startDate"><FaCalendar /></label>
                <input
                  type="text"
                  name="startDate"
                  placeholder="Start Date"
                  id="startDate"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
    
              {/* Delivery Time Input Field */}
              <div className="input-field">
                <label htmlFor="deliveryTime"><FaClock /></label>
                <input
                  type="text"
                  name="deliveryTime"
                  placeholder="Preferred Delivery Time"
                  id="deliveryTime"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.deliveryTime}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Custom Requests Text Area */}
            <div className="input-field text-area">
              <label htmlFor="customRequests"><FaComment /></label>
              <textarea
                cols="30"
                rows="5"
                name="customRequests"
                className="box"
                id="customRequests"
                placeholder="Custom Requests (e.g., special instructions)"
                value={formData.customRequests}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Additional Services Checkboxes */}
            <div className="extra-services">

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="filter-installation"
                  name="additionalServices[]"
                  value="Filter Installation"
                  checked={formData.additionalServices.includes('Filter Installation')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="filter-installation">Filter Installation</label>
                <div className="checkbox-indicator"></div>
              </div>
        
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="water-testing"
                  name="additionalServices[]"
                  value="Water Testing"
                  checked={formData.additionalServices.includes('Water Testing')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="water-testing">Water Testing</label>
                <div className="checkbox-indicator"></div>
              </div>
        
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="dispenser-maintenance"
                  name="additionalServices[]"
                  value="Dispenser Maintenance"
                  checked={formData.additionalServices.includes('Dispenser Maintenance')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="dispenser-maintenance">Dispenser Maintenance</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Order Now'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully!</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default OrderNow;